class BannerCarousel {
  constructor(root) {
    this.root = root;
    this.swiperEl = root.querySelector('[data-banner-carousel-swiper]');
    this.wrapper = this.swiperEl?.querySelector('.swiper-wrapper');
    this.swiper = null;
    this.refreshTimer = null;
    this.swiperWaitTimer = null;
    this.swiperWaitTimeout = null;
    this.generation = 0;
    this.initialized = false;
    if (this.swiperEl?.swiper) this.swiperEl.swiper.destroy(true, true);
    this.slides = Array.from(this.wrapper?.children || []).filter((slide) => slide.classList.contains('banner-carousel__slide'));
    this.activeSlides = [];
    this.onPageVisible = () => { if (!document.hidden) this.refresh(); };
    document.addEventListener('visibilitychange', this.onPageVisible);
    window.addEventListener('pageshow', this.onPageVisible);
    this.refresh();
  }

  // Text settings are interpreted in Peru's fixed UTC-5 time, never the visitor's timezone.
  parseBoundary(dateValue, timeValue) {
    const date = (dateValue || '').trim();
    const time = (timeValue || '').trim();
    if (!date && !time) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) return NaN;
    const timestamp = Date.parse(date + 'T' + time + ':00-05:00');
    if (!Number.isFinite(timestamp)) return NaN;
    return new Date(timestamp - 5 * 60 * 60 * 1000).toISOString().slice(0, 16) === date + 'T' + time ? timestamp : NaN;
  }

  scheduleFor(slide) {
    return {
      start: this.parseBoundary(slide.dataset.bannerStartDate, slide.dataset.bannerStartTime),
      end: this.parseBoundary(slide.dataset.bannerEndDate, slide.dataset.bannerEndTime),
    };
  }

  isActive(slide, now) {
    const { start, end } = this.scheduleFor(slide);
    if (Number.isNaN(start) || Number.isNaN(end)) return false;
    if (start !== null && end !== null && end <= start) return false;
    return (start === null || now >= start) && (end === null || now < end);
  }

  scheduleNext(now) {
    clearTimeout(this.refreshTimer);
    const upcoming = this.slides.flatMap((slide) => {
      const { start, end } = this.scheduleFor(slide);
      return [start, end].filter((time) => Number.isFinite(time) && time > now);
    });
    const untilBoundary = upcoming.length ? Math.min(...upcoming) - now + 50 : 60000;
    this.refreshTimer = setTimeout(() => this.refresh(), Math.max(50, Math.min(60000, untilBoundary)));
  }

  refresh() {
    if (!this.wrapper) return;
    const now = Date.now();
    const next = this.slides.filter((slide) => this.isActive(slide, now));
    const changed = !this.initialized || next.length !== this.activeSlides.length || next.some((slide, i) => slide !== this.activeSlides[i]);

    if (changed) {
      const previous = this.activeSlides[this.swiper?.realIndex ?? this.swiper?.activeIndex ?? 0];
      this.destroySwiper();
      this.slides.forEach((slide) => {
        const visible = next.includes(slide);
        slide.hidden = !visible;
        if (visible) slide.style.removeProperty('display');
        else slide.style.display = 'none';
      });
      this.wrapper.replaceChildren(...next);
      this.activeSlides = next;
      this.root.hidden = next.length === 0;
      this.root.style.display = next.length === 0 ? 'none' : '';
      this.updateControls();
      this.initialized = true;
      if (next.length) {
        const generation = ++this.generation;
        this.waitForSwiper(() => {
          if (generation !== this.generation || !this.activeSlides.length) return;
          try {
            this.swiper = new Swiper(this.swiperEl, this.getConfig());
            const previousIndex = next.indexOf(previous);
            if (previousIndex > 0 && typeof this.swiper.slideToLoop === 'function') {
              this.swiper.slideToLoop(previousIndex, 0, false);
            }
            this.updateCounter();
          } catch (error) {
            // Keep the visible, unenhanced banners if Swiper is unavailable.
          }
        });
      }
    }
    this.scheduleNext(now);
  }

  updateControls() {
    const multiple = this.activeSlides.length > 1;
    this.root.querySelectorAll('[data-banner-carousel-pagination], [data-banner-carousel-counter], [data-banner-carousel-next], [data-banner-carousel-prev]').forEach((control) => {
      control.hidden = !multiple;
      control.style.display = multiple ? '' : 'none';
    });
    const totalEl = this.root.querySelector('.banner-carousel__counter-total');
    if (totalEl) totalEl.textContent = this.activeSlides.length;
  }

  getConfig() {
    const totalSlides = this.activeSlides.length;
    const mobileColumns = parseFloat(this.root.dataset.mobileColumns || '1');
    const desktopColumns = parseFloat(this.root.dataset.desktopColumns || '3');
    const spaceBetween = parseInt(this.root.dataset.spaceBetween || '0', 10);
    const autoRotate = this.root.dataset.autoRotate || 'disabled';
    const enableLoopSetting = this.root.dataset.enableLoop === 'true';
    const showPagination = this.root.dataset.showPagination === 'true';
    const showCounter = this.root.dataset.showCounter === 'true';
    const showNavigation = this.root.dataset.showNavigation === 'true';
    const enableLoop = (enableLoopSetting || autoRotate !== 'disabled') && totalSlides > Math.max(mobileColumns, desktopColumns);
    const config = {
      slidesPerView: mobileColumns,
      spaceBetween,
      loop: enableLoop,
      speed: 600,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      breakpoints: { 768: { slidesPerView: desktopColumns, spaceBetween } },
      on: { slideChange: () => { if (showCounter) this.updateCounter(); } },
    };
    if (autoRotate !== 'disabled' && totalSlides > 1) {
      config.autoplay = {
        delay: parseInt(autoRotate, 10) * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }
    if (showPagination) {
      config.pagination = {
        el: this.root.querySelector('[data-banner-carousel-pagination]'),
        clickable: true,
        dynamicBullets: true,
      };
    }
    if (showNavigation) {
      config.navigation = {
        nextEl: this.root.querySelector('[data-banner-carousel-next]'),
        prevEl: this.root.querySelector('[data-banner-carousel-prev]'),
      };
    }
    return config;
  }

  waitForSwiper(callback) {
    if (typeof Swiper !== 'undefined') { callback(); return; }
    this.swiperWaitTimer = setInterval(() => {
      if (typeof Swiper !== 'undefined') {
        clearInterval(this.swiperWaitTimer);
        this.swiperWaitTimer = null;
        clearTimeout(this.swiperWaitTimeout);
        callback();
      }
    }, 50);
    this.swiperWaitTimeout = setTimeout(() => {
      clearInterval(this.swiperWaitTimer);
      this.swiperWaitTimer = null;
    }, 5000);
  }

  updateCounter() {
    const counterEl = this.root.querySelector('[data-banner-carousel-counter]');
    if (!counterEl || !this.swiper) return;
    const currentEl = counterEl.querySelector('.banner-carousel__counter-current');
    if (!currentEl) return;
    const realIndex = this.swiper.realIndex !== undefined ? this.swiper.realIndex : this.swiper.activeIndex;
    currentEl.textContent = realIndex + 1;
  }

  destroySwiper() {
    this.generation++;
    clearInterval(this.swiperWaitTimer);
    clearTimeout(this.swiperWaitTimeout);
    this.swiperWaitTimer = null;
    this.swiperWaitTimeout = null;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  destroy() {
    clearTimeout(this.refreshTimer);
    this.destroySwiper();
    document.removeEventListener('visibilitychange', this.onPageVisible);
    window.removeEventListener('pageshow', this.onPageVisible);
  }
}

function initBannerCarousels(scope = document) {
  const roots = [];
  if (scope.matches?.('[data-banner-carousel]')) roots.push(scope);
  roots.push(...scope.querySelectorAll('[data-banner-carousel]'));
  roots.forEach((root) => {
    if (root.__bannerCarousel) root.__bannerCarousel.destroy();
    root.__bannerCarousel = new BannerCarousel(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBannerCarousels());
} else {
  initBannerCarousels();
}

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initBannerCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-banner-carousel]');
    if (root?.__bannerCarousel) {
      root.__bannerCarousel.destroy();
      root.__bannerCarousel = null;
    }
  });
}
